import { Alert, Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { GalleryPreview } from '../../types/previewStudio';
import { extractVideoInfo } from '../../util/galleryVideoUtils';

function buildMraidMock(adWidth: number, adHeight: number): string {
  return `<script>
(function() {
  if (typeof window.mraid !== 'undefined') return;
  var mraidState = 'default';
  var eventListeners = {};
  var defaultPosition = { x: 0, y: 0, width: ${adWidth}, height: ${adHeight} };
  var expandedPosition = { x: 0, y: 0, width: 320, height: 480 };
  function fireEvent(name, data) {
    if (eventListeners[name]) {
      eventListeners[name].forEach(function(fn) { try { fn(data); } catch(e) {} });
    }
  }
  window.mraid = {
    VERSION: '2.0',
    getVersion: function() { return '2.0'; },
    getState: function() { return mraidState; },
    addEventListener: function(e, fn) {
      if (!eventListeners[e]) eventListeners[e] = [];
      eventListeners[e].push(fn);
    },
    removeEventListener: function(e, fn) {
      if (eventListeners[e]) {
        var idx = eventListeners[e].indexOf(fn);
        if (idx > -1) eventListeners[e].splice(idx, 1);
      }
    },
    expand: function() {
      if (mraidState === 'default') {
        window.parent.postMessage({ type: 'mraid_expand', width: expandedPosition.width, height: expandedPosition.height }, '*');
        mraidState = 'expanded';
        fireEvent('stateChange', 'expanded');
      }
    },
    close: function() {
      if (mraidState === 'expanded') {
        window.parent.postMessage({ type: 'mraid_close' }, '*');
        mraidState = 'default';
        fireEvent('stateChange', 'default');
      }
    },
    open: function(url) { if (url && url.trim()) window.open(url, '_blank'); },
    getPlacementType: function() { return 'inline'; },
    useCustomClose: function() {},
    setExpandProperties: function(p) {
      if (p.width) expandedPosition.width = p.width;
      if (p.height) expandedPosition.height = p.height;
    },
    getExpandProperties: function() {
      return { width: expandedPosition.width, height: expandedPosition.height, useCustomClose: false, isModal: true };
    },
    getScreenSize: function() { return { width: 375, height: 667 }; },
    getMaxSize: function() { return { width: 375, height: 667 }; },
    getCurrentPosition: function() {
      return mraidState === 'expanded' ? expandedPosition : defaultPosition;
    },
    getDefaultPosition: function() { return defaultPosition; },
    supports: function(f) { return ['sms','tel','calendar','storePicture','inlineVideo'].indexOf(f) > -1; },
    isViewable: function() { return true; },
    playVideo: function(url) { if (url) window.open(url, '_blank'); },
    storePicture: function() {},
    createCalendarEvent: function() {}
  };
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'trigger_mraid_close') { window.mraid.close(); }
  });
  setTimeout(function() { fireEvent('ready'); fireEvent('viewableChange', true); }, 50);
})();
${'</'}script>`;
}

export function buildBlobUrl(preview: GalleryPreview): string | null {
  if (!preview.adm) return null;

  let adm: string;
  try {
    adm = atob(preview.adm);
  } catch {
    return null;
  }

  if (!adm.trim()) return null;

  const w = preview.width || 300;
  const h = preview.height || 250;
  const celtraTag = adm.includes('celtra.com');
  const mraidScript = preview.is_mraid || celtraTag ? buildMraidMock(w, h) : '';
  const celtraCss = celtraTag ? `div[class^="celtra-ad-v"]{width:${w}px!important;height:${h}px!important;overflow:hidden!important;position:relative!important;}` : '';

  const html = `<!DOCTYPE html>
<html style="margin:0;padding:0;width:${w}px;height:${h}px;overflow:hidden;">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=${w},initial-scale=1,maximum-scale=1,user-scalable=no">
<style>
*{box-sizing:border-box;margin:0;padding:0;}
html,body{margin:0!important;padding:0!important;width:${w}px!important;height:${h}px!important;overflow:hidden!important;background:transparent;}
body{display:flex!important;align-items:center!important;justify-content:center!important;position:relative;}
${celtraCss}
</style>
${mraidScript}
</head>
<body>${adm}</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

interface PreviewCardProps {
  preview: GalleryPreview;
  apiHost: string;
}

function isCeltra(preview: GalleryPreview): boolean {
  if (!preview.adm) return false;
  try {
    return atob(preview.adm).includes('celtra.com');
  } catch {
    return false;
  }
}

export default function GalleryPreviewCard({ preview, apiHost }: PreviewCardProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  const celtraTag = isCeltra(preview);
  const videoInfo = preview.is_video ? extractVideoInfo(preview) : null;

  const w = videoInfo?.width || preview.width || 300;
  const h = videoInfo?.height || preview.height || 250;
  const maxCardWidth = 350;

  const [expanded, setExpanded] = useState(false);
  const [expandDims, setExpandDims] = useState({ width: 320, height: 480 });
  const [iframeDims, setIframeDims] = useState({ width: w, height: h });

  const getSrc = useCallback(() => {
    if (preview.adm) {
      const url = buildBlobUrl(preview);
      blobUrlRef.current = url;
      return url;
    }
    if (preview.preview_url) {
      return `${apiHost}${preview.preview_url}`;
    }
    return null;
  }, [preview, apiHost]);

  const [src] = useState(getSrc);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const { data } = event;
      if (!data || !data.type) return;

      if (data.type === 'mraid_expand') {
        const ew = Math.min(data.width || 320, 375);
        const eh = Math.min(data.height || 480, 667);
        setExpandDims({ width: ew, height: eh });
        setIframeDims({ width: ew, height: eh });
        setExpanded(true);
      } else if (data.type === 'mraid_close') {
        setIframeDims({ width: w, height: h });
        setExpanded(false);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [w, h]);

  const handleClose = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'trigger_mraid_close' }, '*');
    }
    setTimeout(() => {
      setIframeDims({ width: w, height: h });
      setExpanded(false);
    }, 100);
  };

  if (preview.status === 'error') {
    return (
      <Alert severity="error" sx={{ flex: 1 }}>
        {preview.error || 'Failed to generate preview'}
      </Alert>
    );
  }

  if (videoInfo) {
    const scale = Math.min(1, maxCardWidth / w);
    return (
      <Box sx={{ width: w * scale, height: h * scale, bgcolor: 'black', mx: 'auto', borderRadius: 1, overflow: 'hidden' }}>
        <video
          src={videoInfo.videoUrl}
          controls
          autoPlay
          muted
          loop
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }

  if (!src) {
    return (
      <Box sx={{ flex: 1, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Preview unavailable</Typography>
      </Box>
    );
  }

  const currentW = iframeDims.width;
  const currentH = iframeDims.height;
  const scale = expanded
    ? Math.min(1, maxCardWidth / expandDims.width)
    : Math.min(1, maxCardWidth / w);
  const displayHeight = currentH * scale;

  return (
    <Box sx={{ width: currentW * scale, overflow: 'hidden', height: displayHeight, position: 'relative', display: 'flex', justifyContent: 'center', transition: 'all 0.2s ease', mx: 'auto' }}>
      {expanded && (
        <Box
          onClick={handleClose}
          sx={{
            position: 'absolute', top: 4, right: 4, zIndex: 10,
            width: 28, height: 28, borderRadius: '50%',
            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 18, lineHeight: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          &times;
        </Box>
      )}
      <Box sx={{ width: currentW, height: currentH, transform: `scale(${scale})`, transformOrigin: 'top center', transition: 'all 0.2s ease' }}>
        <iframe
          ref={iframeRef}
          src={src}
          title={preview.name}
          sandbox={`allow-scripts${!celtraTag ? ' allow-same-origin' : ''} allow-modals allow-popups allow-forms${!celtraTag ? ' allow-top-navigation' : ''} allow-popups-to-escape-sandbox`}
          style={{ width: currentW, height: currentH, border: 'none', display: 'block' }}
        />
      </Box>
    </Box>
  );
}
